import { useEffect, useMemo, useRef, useState } from "react";
import { MusicAttachButton } from "@/components/platform/MusicAttach";
import { encodeMusicTag, type AttachedTrack } from "@/lib/musicTag";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImagePlus, X, Globe, Users, Lock, Send, Loader2, Sparkles, Share2, Copy, Check, Crop } from "lucide-react";
import { toast } from "sonner";
import { useCurrentUser } from "@/hooks/platform/useCurrentUser";
import { useProfile } from "@/hooks/platform/useProfile";
import { useCreatePost } from "@/hooks/platform/usePosts";
import { useMediaUpload } from "@/hooks/platform/useMediaUpload";
import { askAi } from "@/lib/aiText";
import { cropImageFile, ASPECT_RATIOS, type CropAspect } from "@/lib/cropImage";
import { validatePost, CAPTION_LIMIT, MAX_MEDIA } from "@/lib/postValidation";

/** Instagram-style filter presets applied with CSS filters. */
const FILTERS = [
  { id: "original", label: "Original", css: "none" },
  { id: "clarendon", label: "Clarendon", css: "contrast(1.2) saturate(1.35)" },
  { id: "gingham", label: "Gingham", css: "brightness(1.05) hue-rotate(-10deg)" },
  { id: "moon", label: "Moon", css: "grayscale(1) contrast(1.1) brightness(1.1)" },
  { id: "lark", label: "Lark", css: "brightness(1.1) contrast(0.9) saturate(1.1)" },
  { id: "juno", label: "Juno", css: "saturate(1.4) contrast(1.1) sepia(0.1)" },
  { id: "reyes", label: "Reyes", css: "sepia(0.35) brightness(1.1) contrast(0.85)" },
  { id: "willow", label: "Willow", css: "grayscale(0.5) contrast(0.95) brightness(1.05)" },
  { id: "nightclub", label: "Nightclub", css: "contrast(1.3) saturate(1.6) hue-rotate(310deg)" },
] as const;

const AUDIENCES = [
  { id: "public", label: "Everyone", icon: Globe },
  { id: "followers", label: "Followers", icon: Users },
  { id: "private", label: "Only me", icon: Lock },
] as const;

const ASPECTS: CropAspect[] = ["original", "1:1", "4:5", "16:9"];
const DRAFT_KEY = "mcg.quickpost.draft";

interface MediaItem { url: string; file: File }

interface Draft {
  caption: string;
  audience: (typeof AUDIENCES)[number]["id"];
  filter: string;
  aspect: CropAspect;
  savedAt: number;
}

const QuickPost = () => {
  const navigate = useNavigate();
  const { userId } = useCurrentUser();
  const { data: profileData } = useProfile();
  const profile = profileData?.profile;
  const createPost = useCreatePost();
  const { upload } = useMediaUpload("creator-media");
  const fileRef = useRef<HTMLInputElement>(null);

  const [media, setMedia] = useState<MediaItem[]>([]);
  const [active, setActive] = useState(0);
  const [filter, setFilter] = useState<string>("original");
  const [aspect, setAspect] = useState<CropAspect>("original");
  const [zoom, setZoom] = useState(1);
  const [caption, setCaption] = useState("");
  const [audience, setAudience] = useState<(typeof AUDIENCES)[number]["id"]>("public");
  const [posting, setPosting] = useState(false);
  const [track, setTrack] = useState<AttachedTrack | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const activeFilter = useMemo(() => FILTERS.find((f) => f.id === filter) ?? FILTERS[0], [filter]);
  const current = media[active] ?? media[0];

  /* ---------------- Draft autosave + recovery ---------------- */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw) as Draft;
      if (!draft.caption?.trim()) return;
      setCaption(draft.caption);
      setAudience(draft.audience ?? "public");
      setFilter(draft.filter ?? "original");
      setAspect(draft.aspect ?? "original");
      toast("Draft recovered", { description: "We restored your unsaved caption." });
    } catch { /* corrupt draft */ }
  }, []);

  useEffect(() => {
    const id = window.setTimeout(() => {
      const draft: Draft = { caption, audience, filter, aspect, savedAt: Date.now() };
      if (caption.trim()) localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      else localStorage.removeItem(DRAFT_KEY);
    }, 600);
    return () => window.clearTimeout(id);
  }, [caption, audience, filter, aspect]);

  /* ---------------- Media ---------------- */
  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const next = Array.from(files)
      .slice(0, MAX_MEDIA - media.length)
      .map((file) => ({ url: URL.createObjectURL(file), file }));
    if (!next.length) { toast.error(`You can attach up to ${MAX_MEDIA} items.`); return; }
    setMedia((m) => [...m, ...next]);
  };

  const removeAt = (index: number) => {
    setMedia((m) => m.filter((_, i) => i !== index));
    setActive((a) => Math.max(0, a >= index ? a - 1 : a));
  };

  /* ---------------- AI captions ---------------- */
  const suggestCaptions = async () => {
    setSuggesting(true);
    try {
      const text = await askAi([
        {
          role: "user",
          content:
            "Write short social captions for Metro City Groove, a music and nightlife platform. Reply with exactly 3 caption options, one per line, no numbering, no quotes, no markdown, each under 180 characters, with at most 3 hashtags.\n\n" +
            (caption.trim()
              ? `Rewrite and improve this caption in 3 ways: ${caption.trim()}`
              : `Suggest 3 captions for a post with ${media.length} ${media.length === 1 ? "photo/video" : "photos/videos"} about music, gigs or nightlife.`),
        },
      ]);
      const lines = text.split("\n").map((l) => l.replace(/^[-•\d.\s]+/, "").trim()).filter(Boolean).slice(0, 3);
      if (!lines.length) throw new Error("No suggestions returned.");
      setSuggestions(lines);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Caption suggestions unavailable");
    } finally {
      setSuggesting(false);
    }
  };

  /* ---------------- Publish ---------------- */
  const share = async () => {
    if (!userId) { toast.error("Sign in to post"); return; }
    const check = validatePost({ caption, mediaCount: media.length });
    if (!check.ok) { toast.error(check.error); return; }

    setPosting(true);
    try {
      const uploaded: { url: string; type: string; filter: string }[] = [];
      for (const item of media) {
        const isVideo = item.file.type.startsWith("video");
        const file = isVideo
          ? item.file
          : await cropImageFile(item.file, { aspect, zoom, cssFilter: activeFilter.css });
        const url = await upload(file);
        if (url) uploaded.push({ url, type: isVideo ? "video" : "image", filter: activeFilter.css });
      }
      const post = await createPost.mutateAsync({
        author_id: userId,
        content: caption.trim() || null,
        post_type: uploaded.some((u) => u.type === "video") ? "video" : uploaded.length ? "image" : "text",
        media: uploaded as never,
        tags: track ? [encodeMusicTag(track)] : [],
        visibility: audience,
      });
      localStorage.removeItem(DRAFT_KEY);
      toast.success("Posted to your feed");
      const id = (post as { id?: string } | undefined)?.id;
      setShareUrl(`${window.location.origin}/post/${id ?? ""}`.replace(/\/$/, ""));
      setShareOpen(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not publish your post");
    } finally {
      setPosting(false);
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied");
    window.setTimeout(() => setCopied(false), 1500);
  };

  const nativeShare = async () => {
    const data = { title: "Metro City Groove", text: caption.trim() || "New post on Metro City Groove", url: shareUrl };
    if (navigator.share) {
      try { await navigator.share(data); } catch { /* dismissed */ }
    } else {
      await copyLink();
    }
  };

  const name = profile?.full_name || profile?.username || "You";
  const aspectRatio = ASPECT_RATIOS[aspect];

  return (
    <div className="min-h-[calc(100vh-4rem)] w-full bg-background text-foreground">
      <header className="flex items-center justify-between px-4 sm:px-8 h-14 border-b border-border">
        <h1 className="text-base font-semibold">New post</h1>
        <span className={`text-xs ${caption.length > CAPTION_LIMIT ? "text-destructive" : "text-muted-foreground"}`}>
          {caption.length}/{CAPTION_LIMIT}
        </span>
      </header>

      <main className="w-full px-4 sm:px-8 py-6 space-y-6 pb-32">
        {/* Media canvas */}
        <div className="rounded-2xl overflow-hidden border border-border bg-muted/20 min-h-[42vh] lg:min-h-[52vh] flex items-center justify-center relative">
          {!current ? (
            <button type="button" onClick={() => fileRef.current?.click()} className="flex flex-col items-center gap-3 text-muted-foreground hover:text-foreground">
              <ImagePlus className="w-10 h-10" />
              <span className="text-sm">Tap to add photos or a video</span>
            </button>
          ) : current.file.type.startsWith("video") ? (
            <video src={current.url} controls playsInline className="w-full max-h-[60vh] object-contain" style={{ filter: activeFilter.css }} />
          ) : (
            <div
              className="w-full max-h-[60vh] overflow-hidden flex items-center justify-center"
              style={aspectRatio ? { aspectRatio: String(aspectRatio) } : undefined}
            >
              <img
                src={current.url}
                alt="Selected media"
                className="w-full h-full object-cover"
                style={{ filter: activeFilter.css, transform: `scale(${zoom})` }}
              />
            </div>
          )}

          {current && (
            <button
              onClick={() => removeAt(active)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/70 text-white flex items-center justify-center"
              aria-label="Remove this item"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => addFiles(e.target.files)} />

        {/* Media strip — swipe between attachments, add more, Instagram style */}
        {media.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {media.map((m, i) => (
              <button
                key={m.url}
                type="button"
                onClick={() => setActive(i)}
                className={`relative shrink-0 rounded-lg border-2 ${i === active ? "border-primary" : "border-transparent"}`}
                aria-label={`Select item ${i + 1}`}
              >
                {m.file.type.startsWith("video")
                  ? <video src={m.url} className="w-16 h-16 rounded-md object-cover" muted />
                  : <img src={m.url} alt={`Selected ${i + 1}`} className="w-16 h-16 rounded-md object-cover" style={{ filter: activeFilter.css }} />}
              </button>
            ))}
            {media.length < MAX_MEDIA && (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="shrink-0 w-16 h-16 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground"
                aria-label="Add more media"
              >
                <ImagePlus className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Crop + zoom */}
        {current && !current.file.type.startsWith("video") && (
          <div className="space-y-3">
            <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
              <Crop className="w-3.5 h-3.5" />Crop & preview
            </p>
            <div className="flex flex-wrap gap-2">
              {ASPECTS.map((a) => (
                <Button key={a} size="sm" variant={aspect === a ? "default" : "outline"} className="rounded-full" onClick={() => setAspect(a)}>
                  {a === "original" ? "Original" : a}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-3 max-w-sm">
              <span className="text-xs text-muted-foreground">Zoom</span>
              <Slider value={[zoom]} min={1} max={3} step={0.05} onValueChange={([v]) => setZoom(v)} />
              <span className="text-xs tabular-nums text-muted-foreground">{zoom.toFixed(2)}×</span>
            </div>
          </div>
        )}

        {/* Filters */}
        {current && (
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Filters</p>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {FILTERS.map((f) => (
                <button key={f.id} type="button" onClick={() => setFilter(f.id)} className="shrink-0 text-center">
                  <img
                    src={current.url}
                    alt={f.label}
                    className={`w-16 h-16 rounded-lg object-cover border-2 ${filter === f.id ? "border-primary" : "border-transparent"}`}
                    style={{ filter: f.css }}
                  />
                  <span className={`block mt-1 text-[11px] ${filter === f.id ? "text-primary" : "text-muted-foreground"}`}>{f.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Caption */}
        <div className="flex gap-3">
          <Avatar className="w-10 h-10">
            <AvatarImage src={profile?.avatar_url ?? undefined} alt={name} />
            <AvatarFallback className="bg-primary text-primary-foreground">{name.charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <Textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, CAPTION_LIMIT))}
            placeholder="Write a caption…"
            className="bg-transparent border-none resize-none min-h-[120px] p-0 text-base placeholder:text-muted-foreground focus-visible:ring-0"
          />
        </div>

        <MusicAttachButton value={track} onChange={setTrack} />

        {/* AI captions */}
        <div className="space-y-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={suggestCaptions} disabled={suggesting}>
            {suggesting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Suggest captions
          </Button>
          {suggestions.length > 0 && (
            <div className="space-y-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => { setCaption(s.slice(0, CAPTION_LIMIT)); toast("Caption applied — edit it before posting."); }}
                  className="block w-full text-left text-sm rounded-xl border border-border p-3 hover:border-primary"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Audience */}
        <div className="flex flex-wrap gap-2">
          {AUDIENCES.map(({ id, label, icon: Icon }) => (
            <Button key={id} size="sm" variant={audience === id ? "default" : "outline"} onClick={() => setAudience(id)} className="rounded-full">
              <Icon className="w-3.5 h-3.5 mr-1" />{label}
            </Button>
          ))}
        </div>

        {/* Share */}
        <Button onClick={share} disabled={posting} className="w-full h-12 rounded-xl text-base">
          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />Share post</>}
        </Button>
      </main>

      {/* Share sheet */}
      <Dialog open={shareOpen} onOpenChange={(open) => { setShareOpen(open); if (!open) navigate(-1); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Post shared</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl border border-border overflow-hidden">
              {current && !current.file.type.startsWith("video") && (
                <img src={current.url} alt="Social preview" className="w-full h-40 object-cover" style={{ filter: activeFilter.css }} />
              )}
              <div className="p-3 space-y-1">
                <p className="text-sm font-medium">Metro City Groove</p>
                <p className="text-xs text-muted-foreground line-clamp-2">{caption.trim() || "New post on Metro City Groove"}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button className="flex-1" onClick={nativeShare}><Share2 className="w-4 h-4 mr-2" />Share</Button>
              <Button variant="outline" className="flex-1" onClick={copyLink}>
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}Copy link
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuickPost;
